import getApiConfig from '../config/apiConfig';
import { getToken } from './authService';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

// Timezone conversion utilities
const convertUTCToEastern = (utcDateTimeString) => {
  if (!utcDateTimeString) return null;
  
  // Graph API returns UTC times, so parse it directly
  const date = new Date(utcDateTimeString);
  if (isNaN(date.getTime())) return null;

  // Format in Eastern timezone
  return new Date(date.toLocaleString('en-US', {
    timeZone: 'America/New_York'
  }));
};

const getEasternDateString = (utcDateTimeString) => {
  if (!utcDateTimeString) return null;
  
  // Graph API returns UTC times, so parse it directly
  const date = new Date(utcDateTimeString);
  if (isNaN(date.getTime())) return null;

  // Convert to Eastern time
  const easternDate = new Date(date.toLocaleString('en-US', {
    timeZone: 'America/New_York'
  }));

  // Format as YYYY-MM-DD
  const year = easternDate.getFullYear();
  const month = String(easternDate.getMonth() + 1).padStart(2, '0');
  const day = String(easternDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Helper function to calculate hours between two dates and round up to nearest 0.25
const calculateRoundedHours = (startTimeUTC, endTimeUTC) => {
  if (!startTimeUTC || !endTimeUTC) return 0;
  
  // Parse UTC times directly
  const startDate = new Date(startTimeUTC);
  const endDate = new Date(endTimeUTC);
  
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
  
  // Calculate difference in hours
  const diffMs = endDate - startDate;
  const diffHours = diffMs / (1000 * 60 * 60);
  
  // Round up to nearest 0.25 hour
  return Math.ceil(diffHours * 4) / 4;
};

// Get all calendars accessible to the current user (including shared calendars)
export const getAccessibleCalendars = async () => {
  try {
    const { apiBaseUrl } = getApiConfig();
    
    const response = await fetch(`${apiBaseUrl}/graph/me/calendars`);
    
    if (response.ok) {
      const result = await response.json();
      return result.value || [];
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendars API error response:', errorText);
      return [];
    }
  } catch (error) {
    console.error('GraphService: Error fetching accessible calendars:', error);
    return [];
  }
};

// Get calendar events from a specific calendar
export const getCalendarEventsFromCalendar = async (calendarId, startDate, endDate) => {
  try {
    const { apiBaseUrl } = getApiConfig();

    // Format dates for API (ISO 8601 format)
    const startDateTime = `${startDate}T00:00:00.000Z`;
    const endDateTime = `${endDate}T23:59:59.999Z`;
    
    // Build URL with minimal fields selection
    const select = 'subject,start,end,showAs,isAllDay';
    const url = `${apiBaseUrl}/graph/users/${encodeURIComponent(calendarId)}/calendar/calendarview` +
      `?startDateTime=${startDateTime}` +
      `&endDateTime=${endDateTime}` +
      `&$select=${select}` +
      '&$orderby=start/dateTime';
      
    const response = await fetch(url);
    
    if (response.ok) {
      const result = await response.json();
      
      // Transform calendar events to time entry format
      const calendarTimeEntries = result.value
        .filter(event => {
          // More lenient filtering - only exclude all-day events and explicitly "free" events
          const shouldInclude = !event.isAllDay && event.showAs !== 'free';
          
          if (!shouldInclude) {
            console.log(`GraphService: Filtering out event: "${event.subject}" - isAllDay: ${event.isAllDay}, showAs: ${event.showAs}`);
          }
          
          return shouldInclude;
        })
        .map(event => {
          const startTime = event.start.dateTime;
          const endTime = event.end.dateTime;
          const eventDate = getEasternDateString(startTime);
          const hours = calculateRoundedHours(startTime, endTime);
          
          // Format times in Eastern timezone
          const startEasternTime = new Date(startTime).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit'
          });
          const endEasternTime = new Date(endTime).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit'
          });
          const timeDisplay = `${startEasternTime} - ${endEasternTime}`;
          
          return {
            id: `calendar-${event.id || Math.random().toString(36).substr(2, 9)}`,
            date: eventDate,
            description: `${event.subject || 'Untitled Event'} (${timeDisplay})`,
            hours: hours,
            isCalendarEvent: true,
            showAs: event.showAs
          };
        });
      
      console.log(`GraphService: Transformed ${calendarTimeEntries.length} calendar events to time entries`);
      return calendarTimeEntries;
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendar events API error response:', errorText);
      
      // Log detailed error for debugging
      if (response.status === 403) {
        console.error('GraphService: Permission denied. Please check:');
        console.error('1. The user has shared their calendar with you');
        console.error('2. The application has the following Graph API permissions:');
        console.error('   - Calendars.Read or Calendars.ReadWrite');
        console.error('   - User.Read');
      }
      return [];
    }
  } catch (error) {
    console.error('GraphService: Error fetching calendar events:', error);
    return [];
  }
};

// Get calendar events from all accessible calendars (including shared ones)
const getCalendarEventsFromAllAccessibleCalendars = async (startDate, endDate) => {
  try {
    console.log('GraphService: Getting calendar events from all accessible calendars');
    
    // Get all accessible calendars
    const calendars = await getAccessibleCalendars();
    console.log(`GraphService: Found ${calendars.length} accessible calendars`);
    
    // Get events from each calendar
    const allCalendarEvents = [];
    for (const calendar of calendars) {
      console.log(`GraphService: Fetching events from calendar: ${calendar.name} (${calendar.id}) - Owner: ${calendar.owner?.name || 'Unknown'}`);
      
      // Get events from this specific calendar
      const events = await getCalendarEventsFromCalendar(calendar.id, startDate, endDate);
      
      // Add calendar information to each event
      const eventsWithCalendarInfo = events.map(event => ({
        ...event,
        calendarName: calendar.name,
        calendarOwner: calendar.owner?.name || 'Unknown',
        calendarOwnerEmail: calendar.owner?.address || 'Unknown'
      }));
      
      allCalendarEvents.push(...eventsWithCalendarInfo);
    }
    
    console.log(`GraphService: Retrieved ${allCalendarEvents.length} total calendar events from ${calendars.length} calendars`);
    return allCalendarEvents;
  } catch (error) {
    console.error('GraphService: Error getting calendar events from all accessible calendars:', error);
    return [];
  }
};

// Get calendar events for a specific date range (supports both current user and other users)
export const getCalendarEvents = async (startDate, endDate, userId = null) => {
  try {
    console.log('GraphService: Getting calendar events from', startDate, 'to', endDate, 'for user:', userId || 'current user');
    const { apiBaseUrl } = getApiConfig();
    
    // If no userId is specified, get events from all accessible calendars (including shared ones)
    if (!userId || userId === 'current-user') {
      console.log('GraphService: Getting events from all accessible calendars for current user');
      return await getCalendarEventsFromAllAccessibleCalendars(startDate, endDate);
    }
    
    // For specific users, try the direct approach (works for delegated calendars)
    console.log('GraphService: Getting events via direct access for user:', userId);

    // Format dates for API (ISO 8601 format)
    const startDateTime = `${startDate}T00:00:00.000Z`;
    const endDateTime = `${endDate}T23:59:59.999Z`;
    
    // Access another user's calendar directly (for delegated calendars)
    const url = `${apiBaseUrl}/graph/users/${encodeURIComponent(userId)}/calendarview?startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    console.log('GraphService: Making direct calendar request to:', url);
    
    const response = await fetch(url);
    console.log('GraphService: Direct calendar response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: Direct calendar events result:', result);
      
      // Transform calendar events to time entry format
      const calendarTimeEntries = result.value
        .filter(event => {
          // More lenient filtering - only exclude all-day events and explicitly "free" events
          const shouldInclude = !event.isAllDay && event.showAs !== 'free';
          
          if (!shouldInclude) {
            console.log(`GraphService: Filtering out event: "${event.subject}" - isAllDay: ${event.isAllDay}, showAs: ${event.showAs}`);
          } else {
            console.log(`GraphService: Including event: "${event.subject}" - showAs: ${event.showAs}, start: ${event.start.dateTime}, end: ${event.end.dateTime}`);
          }
          
          return shouldInclude;
        })
        .map(event => {
          const startTime = event.start.dateTime;
          const endTime = event.end.dateTime;
          const eventDate = getEasternDateString(startTime);
          const hours = calculateRoundedHours(startTime, endTime);
          
          // Get formatted times in Eastern timezone for display
          const startEastern = convertUTCToEastern(startTime);
          const endEastern = convertUTCToEastern(endTime);
          const timeDisplay = startEastern && endEastern ? 
            `${startEastern.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} - ${endEastern.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })}` : '';
          
          console.log(`GraphService: Transforming event "${event.subject}" on ${eventDate} (${timeDisplay}) for ${hours} hours`);
          
          return {
            id: `calendar-${event.id || Math.random().toString(36).substr(2, 9)}`,
            userId: userId,
            date: eventDate,
            description: `${event.subject || 'Untitled Event'} (${timeDisplay})`,
            hours: hours,
            source: 'calendar',
            isCalendarEvent: true,
            startTime: startTime,
            endTime: endTime,
            showAs: event.showAs,
            createdDate: new Date().toISOString(),
            modifiedDate: new Date().toISOString()
          };
        });
      
      console.log(`GraphService: Transformed ${calendarTimeEntries.length} calendar events to time entries`);
      return calendarTimeEntries;
    } else {
      const errorText = await response.text();
      console.error('GraphService: Direct calendar API error response:', errorText);
      return [];
    }
  } catch (error) {
    console.error('GraphService: Error getting calendar events:', error);
    return [];
  }
};

// Get calendar events for a specific user and month
export const getCalendarEventsForUserAndMonth = async (userId, year, month) => {
  try {
    // Calculate start and end dates in Eastern timezone
    // Start at 04:00:00 UTC (00:00:00 Eastern) on the first day of the month
    const startDate = new Date(Date.UTC(year, month, 1, 4, 0, 0));
    
    // End at 03:59:59 UTC (23:59:59 Eastern) on the last day of the month
    const lastDay = new Date(Date.UTC(year, month + 1, 0, 3, 59, 59));

    // Format dates for API request
    const startDateTime = startDate.toISOString();
    const endDateTime = lastDay.toISOString();

    const requestBody = {
      schedules: [userId],
      startTime: {
        dateTime: startDateTime,
        timeZone: 'America/New_York'
      },
      endTime: {
        dateTime: endDateTime,
        timeZone: 'America/New_York'
      },
      availabilityViewInterval: 60
    };

    const token = await getToken();
    const response = await fetch(
      'https://graph.microsoft.com/v1.0/me/calendar/getSchedule',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Prefer': 'outlook.timezone="America/New_York"'
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('GraphService: Error response:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`Failed to get calendar events: ${errorText}`);
    }

    const data = await response.json();

    // Transform schedule data into calendar events
    const events = [];
    if (data.value && data.value.length > 0) {
      const schedule = data.value[0]; // We only requested one user's schedule
      if (schedule.scheduleItems) {
        events.push(...schedule.scheduleItems.map(item => {
          // Convert start and end times to Eastern timezone
          const startTime = new Date(item.start.dateTime).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit'
          });
          const endTime = new Date(item.end.dateTime).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit'
          });

          // Calculate hours (rounded to nearest 0.25)
          const startMs = new Date(item.start.dateTime).getTime();
          const endMs = new Date(item.end.dateTime).getTime();
          const durationHours = (endMs - startMs) / (1000 * 60 * 60);
          const roundedHours = Math.round(durationHours * 4) / 4;

          // Get date in YYYY-MM-DD format in Eastern timezone
          const eventDate = new Date(item.start.dateTime).toLocaleDateString('en-US', {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
          const [month, day, year] = eventDate.split('/');
          const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

          return {
            id: `calendar-${Math.random().toString(36).substr(2, 9)}`,
            date: formattedDate,
            description: `${item.subject || 'Busy'} (${startTime} - ${endTime}) *Read-only*`,
            hours: roundedHours,
            isCalendarEvent: true,
            startTime: item.start.dateTime,
            endTime: item.end.dateTime,
            showAs: item.status
          };
        }));
      }
    }

    return events;
  } catch (error) {
    console.error('GraphService: Error getting calendar events:', error);
    throw error;
  }
};

// Get user by email using Microsoft Graph directly
export const getUserByEmail = async (email) => {
  try {
    const { getToken } = await import('./authService');
    const accessToken = await getToken();
    
    const response = await fetch(`https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      const errorText = await response.text();
      console.error('GraphService: User API error response:', errorText);
      return null;
    }
  } catch (error) {
    console.error('GraphService: Error getting user by email:', error);
    return null;
  }
};

// Get cached user info
export const getCachedUserInfo = async (email) => {
  try {
    const userInfo = await getUserByEmail(email);
    return userInfo;
  } catch (error) {
    console.error('GraphService: Error getting cached user info:', error);
    return null;
  }
};

// Get cached user photo
export const getCachedUserPhoto = async (email) => {
  try {
    return null;
  } catch (error) {
    console.error('GraphService: Error getting cached user photo:', error);
    return null;
  }
};

// Test calendar access
export const testCalendarAccess = async () => {
  try {
    console.log('GraphService: Testing calendar access');
    const { apiBaseUrl } = getApiConfig();
    
    const response = await fetch(`${apiBaseUrl}/graph/calendar/test`);
    console.log('GraphService: Calendar test response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: Calendar test result:', result);
      return result;
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendar test API error response:', errorText);
      return null;
    }
  } catch (error) {
    console.error('GraphService: Error testing calendar access:', error);
    return null;
  }
};

// Get calendar availability for user and month
export const getCalendarAvailabilityForUserAndMonth = async (userId, year, month) => {
  try {
    console.log('GraphService: Getting calendar availability for user', userId, 'in', year, month);
    
    // Import getToken from authService
    const { getToken } = await import('./authService');
    const accessToken = await getToken();
    
    // Calculate start and end dates for the month
    const startDate = new Date(year, month, 1).toISOString();
    const endDate = new Date(year, month + 1, 0).toISOString();
    
    // Call Microsoft Graph directly using the schedule API
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(userId)}/calendar/getSchedule`, 
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          schedules: [userId],
          startTime: {
            dateTime: startDate,
            timeZone: 'UTC'
          },
          endTime: {
            dateTime: endDate,
            timeZone: 'UTC'
          },
          availabilityViewInterval: 60
        })
      }
    );
    
    console.log('GraphService: Calendar availability response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: Calendar availability result:', result);
      
      // Transform schedule response to time entry format
      const availabilityEntries = result.value?.[0]?.scheduleItems || [];
      
      return availabilityEntries.map(item => {
        const startTime = item.start.dateTime;
        const endTime = item.end.dateTime;
        const eventDate = getEasternDateString(startTime);
        const hours = calculateRoundedHours(startTime, endTime);
        
        // Get formatted times in Eastern timezone for display
        const startEastern = convertUTCToEastern(startTime);
        const endEastern = convertUTCToEastern(endTime);
        const timeDisplay = startEastern && endEastern ? 
          `${startEastern.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} - ${endEastern.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })}` : '';
        
        return {
          id: `calendar-${Math.random().toString(36).substr(2, 9)}`,
          userId: userId,
          date: eventDate,
          description: `${item.subject || 'Busy'} (${timeDisplay})`,
          hours: hours,
          source: 'calendar',
          isCalendarEvent: true,
          startTime: startTime,
          endTime: endTime,
          showAs: item.status,
          calendarId: 'schedule'
        };
      });
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendar availability API error response:', errorText);
      return [];
    }
  } catch (error) {
    console.error('GraphService: Error getting calendar availability:', error);
    return [];
  }
};

// Test calendar access for users
export const testCalendarAccessForUsers = async () => {
  try {
    console.log('GraphService: Testing calendar access for users');
    const { apiBaseUrl } = getApiConfig();
    
    const response = await fetch(`${apiBaseUrl}/graph/calendar/test/users`);
    console.log('GraphService: Calendar test response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: Calendar test result:', result);
      return result;
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendar test API error response:', errorText);
      return null;
    }
  } catch (error) {
    console.error('GraphService: Error testing calendar access for users:', error);
    return null;
  }
};

// Get current user
export const getCurrentUser = async () => {
  try {
    console.log('GraphService: Getting current user');
    const { apiBaseUrl } = getApiConfig();
    
    const response = await fetch(`${apiBaseUrl}/graph/me`);
    console.log('GraphService: User response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: User result:', result);
      return result;
    } else {
      const errorText = await response.text();
      console.error('GraphService: User API error response:', errorText);
      return null;
    }
  } catch (error) {
    console.error('GraphService: Error getting current user:', error);
    return null;
  }
};

// Debug calendar access
export const debugCalendarAccess = async () => {
  try {
    console.log('GraphService: Debugging calendar access');
    const { apiBaseUrl } = getApiConfig();
    
    const response = await fetch(`${apiBaseUrl}/graph/calendar/debug`);
    console.log('GraphService: Calendar debug response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: Calendar debug result:', result);
      return result;
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendar debug API error response:', errorText);
      return null;
    }
  } catch (error) {
    console.error('GraphService: Error debugging calendar access:', error);
    return null;
  }
};

// Get calendar events for a specific user
export const getCalendarEventsForUser = async (userEmail, startDate, endDate) => {
  try {
    console.log('GraphService: Getting calendar events for user', userEmail);
    const { apiBaseUrl } = getApiConfig();

    // Format dates for API (ISO 8601 format)
    const startDateTime = `${startDate}T00:00:00.000Z`;
    const endDateTime = `${endDate}T23:59:59.999Z`;
    
    // Build URL with minimal fields selection
    const select = 'subject,start,end,showAs,isAllDay';
    const url = `${apiBaseUrl}/graph/users/${encodeURIComponent(userEmail)}/calendar/calendarview` +
      `?startDateTime=${startDateTime}` +
      `&endDateTime=${endDateTime}` +
      `&$select=${select}` +
      '&$orderby=start/dateTime';
      
    console.log('GraphService: Making calendar events request to:', url);
    
    const response = await fetch(url);
    console.log('GraphService: Calendar events response status:', response.status);
    
    if (response.ok) {
      const result = await response.json();
      console.log('GraphService: Calendar events result count:', result.value?.length || 0);
      
      // Transform calendar events to time entry format
      const calendarTimeEntries = result.value
        .filter(event => !event.isAllDay && event.showAs !== 'free')
        .map(event => {
          const startTime = event.start.dateTime;
          const endTime = event.end.dateTime;
          const eventDate = getEasternDateString(startTime);
          const hours = calculateRoundedHours(startTime, endTime);
          
          // Format times in Eastern timezone
          const startEasternTime = new Date(startTime).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit'
          });
          const endEasternTime = new Date(endTime).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit'
          });
          const timeDisplay = `${startEasternTime} - ${endEasternTime}`;
          
          return {
            id: `calendar-${event.id || Math.random().toString(36).substr(2, 9)}`,
            date: eventDate,
            description: `${event.subject || 'Untitled Event'} (${timeDisplay})`,
            hours: hours,
            isCalendarEvent: true,
            showAs: event.showAs
          };
        });
      
      console.log(`GraphService: Transformed ${calendarTimeEntries.length} calendar events to time entries`);
      return calendarTimeEntries;
    } else {
      const errorText = await response.text();
      console.error('GraphService: Calendar events API error response for user', userEmail, ':', errorText);
      
      // Log detailed error for debugging
      if (response.status === 403) {
        console.error(`GraphService: Permission denied for user ${userEmail}. Please check:`);
        console.error('1. The user has shared their calendar with you');
        console.error('2. The application has the following Graph API permissions:');
        console.error('   - Calendars.Read or Calendars.ReadWrite');
        console.error('   - User.Read');
      }
      return [];
    }
  } catch (error) {
    console.error('GraphService: Error fetching calendar events for user', userEmail, ':', error);
    return [];
  }
}; 